import { useEffect, useState } from 'react'
import {
  ApiError,
  api,
  type BillingSummary,
  type CustomerRow,
  type EntitlementRung,
  type PlanRow
} from './api'
import Icon from './Icons'

/**
 * Accounts, what they have been sold, and when it runs out.
 *
 * Two audiences in one view. Staff manage the catalogue and sell; a customer's
 * own operator sees only their own account — deliberately, because "when does
 * our licence end and how many cameras did we pay for" is the question that
 * otherwise arrives by phone.
 */

/** What each rung has actually taken away. Wording matches the device banner. */
const RUNG_TEXT: Record<EntitlementRung, string> = {
  full: 'in date',
  warned: 'expired — nothing withheld yet',
  featuresWithheld: 'expired — exports withheld',
  retentionCut: 'expired — recordings kept 1 day',
  silent: 'expired — no configuration served'
}

const RUNG_CLASS: Record<EntitlementRung, string> = {
  full: 'live',
  warned: 'warn',
  featuresWithheld: 'warn',
  retentionCut: 'warn',
  silent: 'off'
}

function baht(satang: number): string {
  return (satang / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function RungPill({ rung }: { rung: EntitlementRung }): React.JSX.Element {
  return <span className={`pill ${RUNG_CLASS[rung]}`}>{RUNG_TEXT[rung]}</span>
}

export default function BillingView(): React.JSX.Element {
  const [customers, setCustomers] = useState<CustomerRow[]>([])
  const [unassigned, setUnassigned] = useState(0)
  const [plans, setPlans] = useState<PlanRow[]>([])
  /** Set when this operator is a customer's, not staff: their own account only. */
  const [mine, setMine] = useState<BillingSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [showCustomer, setShowCustomer] = useState(false)
  const [customerName, setCustomerName] = useState('')
  const [customerNote, setCustomerNote] = useState('')

  const [showPlan, setShowPlan] = useState(false)
  const [planCode, setPlanCode] = useState('')
  const [planName, setPlanName] = useState('')
  const [planPrice, setPlanPrice] = useState('')
  const [planRetention, setPlanRetention] = useState('30')
  const [planFeatures, setPlanFeatures] = useState('recording, export')

  /** Which customer's renewal form is open. */
  const [selling, setSelling] = useState<number | null>(null)
  const [sellPlan, setSellPlan] = useState<number | null>(null)
  const [sellEnds, setSellEnds] = useState('')
  const [sellQuota, setSellQuota] = useState('0')
  const [sellPo, setSellPo] = useState('')

  const refresh = async (): Promise<void> => {
    try {
      const [c, p] = await Promise.all([api.listCustomers(), api.listPlans()])
      setCustomers(c.customers)
      setUnassigned(c.unassigned)
      setPlans(p.plans)
      setMine(null)
      setError(null)
    } catch (err) {
      // A customer's operator is refused the fleet-wide lists, which is correct
      // rather than an error — fall back to the one account they do own.
      if (err instanceof ApiError && err.code === 'forbidden') {
        try {
          setMine(await api.billingSummary(0))
          setError(null)
          return
        } catch (inner) {
          setError(inner instanceof Error ? inner.message : String(inner))
          return
        }
      }
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    try {
      await fn()
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const openSale = (c: CustomerRow): void => {
    setSelling(c.id)
    setSellPlan(c.subscription?.planId ?? plans.find((p) => p.active)?.id ?? null)
    // A renewal defaults to a year past whatever it currently ends, so the
    // common case is one click and the lapsed days are not donated back.
    const from = c.subscription ? new Date(c.subscription.endsAt) : new Date()
    from.setFullYear(from.getFullYear() + 1)
    setSellEnds(from.toISOString().slice(0, 10))
    setSellQuota(String(c.subscription?.deviceQuota ?? 0))
    setSellPo(c.subscription?.poNote ?? '')
  }

  const sell = async (): Promise<void> => {
    if (selling === null || sellPlan === null) return
    const quota = Number(sellQuota)
    await act(() =>
      api.setSubscription(
        selling,
        sellPlan,
        sellEnds,
        Number.isFinite(quota) ? quota : 0,
        sellPo
      )
    )
    setSelling(null)
  }

  const createPlan = async (): Promise<void> => {
    const price = Math.round(Number(planPrice) * 100)
    const retention = Number(planRetention)
    await act(() =>
      api.createPlan(
        planCode,
        planName,
        Number.isFinite(price) ? price : 0,
        Number.isFinite(retention) ? retention : 0,
        planFeatures
          .split(',')
          .map((f) => f.trim())
          .filter(Boolean)
      )
    )
    setShowPlan(false)
    setPlanCode('')
    setPlanName('')
    setPlanPrice('')
  }

  // ---- a customer's own operator sees only this ----
  if (mine) {
    const sub = mine.subscription
    return (
      <div className="stack">
        {error && <div className="banner error">{error}</div>}
        <section className="card">
          <div className="card-head">
            <h2>{mine.customer.name}</h2>
            {sub && <RungPill rung={sub.rung} />}
          </div>
          {sub ? (
            <div className="summary">
              <div>
                <span>Plan</span>
                <strong>{sub.planName ?? sub.planCode ?? '—'}</strong>
              </div>
              <div>
                <span>Runs to</span>
                <strong>{new Date(sub.endsAt).toLocaleDateString()}</strong>
              </div>
              <div>
                <span>Cameras</span>
                <strong>
                  {mine.deviceCount}
                  {sub.deviceQuota ? ` of ${sub.deviceQuota}` : ' · no limit'}
                </strong>
              </div>
            </div>
          ) : (
            <p className="hint">
              No licence period is set on this account, so nothing expires. Your devices
              may still carry their own dates.
            </p>
          )}
          {mine.overQuota && (
            <div className="banner">
              <Icon name="alert" />
              <div>
                You are running {mine.deviceCount} cameras on a licence for{' '}
                {sub?.deviceQuota}. Nothing has been switched off — get in touch and we
                will put it right on the next invoice.
              </div>
            </div>
          )}
        </section>
      </div>
    )
  }

  // ---- staff ----
  return (
    <div className="stack">
      {error && <div className="banner error">{error}</div>}

      <section className="card">
        <div className="card-head">
          <h2>Accounts</h2>
          <button onClick={() => setShowCustomer((v) => !v)}>
            {showCustomer ? 'Close' : 'Add account'}
          </button>
        </div>

        {showCustomer && (
          <div className="stack-tight">
            <input
              placeholder="Account name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
            <input
              placeholder="Contract or PO reference (optional)"
              value={customerNote}
              onChange={(e) => setCustomerNote(e.target.value)}
            />
            <div className="inline">
              <button
                className="primary"
                disabled={!customerName.trim()}
                onClick={() =>
                  void act(async () => {
                    await api.createCustomer(customerName, customerNote)
                    setCustomerName('')
                    setCustomerNote('')
                    setShowCustomer(false)
                  })
                }
              >
                Create
              </button>
            </div>
          </div>
        )}

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Account</th>
                <th>Plan</th>
                <th>Runs to</th>
                <th>Cameras</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className={c.active ? undefined : 'muted'}>
                  <td>
                    <strong>{c.name}</strong>
                    {c.note && <div className="sub">{c.note}</div>}
                  </td>
                  <td>
                    {c.subscription ? (
                      <>
                        {c.subscription.planName ?? c.subscription.planCode}
                        <div className="sub">
                          <RungPill rung={c.subscription.rung} />
                        </div>
                      </>
                    ) : (
                      <span className="sub">nothing sold — never expires</span>
                    )}
                  </td>
                  <td className="sub">
                    {c.subscription
                      ? new Date(c.subscription.endsAt).toLocaleDateString()
                      : '—'}
                    {c.subscription?.poNote && (
                      <div className="sub">{c.subscription.poNote}</div>
                    )}
                  </td>
                  <td className="sub">
                    {c.deviceCount}
                    {c.subscription?.deviceQuota
                      ? ` / ${c.subscription.deviceQuota}`
                      : ''}
                    {c.overQuota && <span className="badge">over</span>}
                  </td>
                  <td>
                    <div className="inline">
                      <button className="small" onClick={() => openSale(c)}>
                        <Icon name="clock" />
                        {c.subscription ? 'Renew' : 'Sell'}
                      </button>
                      {c.subscription && (
                        <button
                          className={
                            c.subscription.status === 'suspended' ? '' : 'danger'
                          }
                          onClick={() =>
                            void act(() =>
                              api.setSubscriptionStatus(
                                c.id,
                                c.subscription?.status === 'suspended'
                                  ? 'active'
                                  : 'suspended'
                              )
                            )
                          }
                        >
                          {c.subscription.status === 'suspended'
                            ? 'Unsuspend'
                            : 'Suspend'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selling !== null && (
          <div className="stack-tight">
            <h3>{customers.find((c) => c.id === selling)?.name}</h3>
            <select
              value={sellPlan ?? ''}
              onChange={(e) => setSellPlan(Number(e.target.value))}
            >
              {plans
                .filter((p) => p.active || p.id === sellPlan)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — ฿{baht(p.priceMonthlySatang)}/camera/month
                  </option>
                ))}
            </select>
            <input
              type="date"
              value={sellEnds}
              onChange={(e) => setSellEnds(e.target.value)}
            />
            <input
              type="number"
              min={0}
              placeholder="Cameras paid for — 0 for no limit"
              value={sellQuota}
              onChange={(e) => setSellQuota(e.target.value)}
            />
            <input
              placeholder="PO or contract reference"
              value={sellPo}
              onChange={(e) => setSellPo(e.target.value)}
            />
            <div className="inline">
              <button
                className="primary"
                disabled={sellPlan === null || !sellEnds}
                onClick={() => void sell()}
              >
                Save
              </button>
              <button onClick={() => setSelling(null)}>Cancel</button>
            </div>
            <p className="hint">
              A renewal is written as a new record and the previous one is kept, so what
              was sold last year survives this year&rsquo;s sale. It starts where the last
              one ended, so renewing late does not give the lapsed days back.
            </p>
          </div>
        )}

        <p className="hint">
          {unassigned > 0
            ? `${unassigned} device${unassigned === 1 ? '' : 's'} not assigned to any account — visible to staff only.`
            : 'Every device belongs to an account.'}{' '}
          An account with nothing sold to it never expires, which is where everything
          predating plans sits.
        </p>
      </section>

      <section className="card">
        <div className="card-head">
          <h2>Plans</h2>
          <button onClick={() => setShowPlan((v) => !v)}>
            {showPlan ? 'Close' : 'Add plan'}
          </button>
        </div>

        {showPlan && (
          <div className="stack-tight">
            <input
              placeholder="Code — basic, standard, evidence"
              value={planCode}
              onChange={(e) => setPlanCode(e.target.value)}
            />
            <input
              placeholder="Name shown on paperwork"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
            />
            <input
              type="number"
              min={0}
              placeholder="Baht per camera per month"
              value={planPrice}
              onChange={(e) => setPlanPrice(e.target.value)}
            />
            <input
              type="number"
              min={0}
              placeholder="Days of recording kept"
              value={planRetention}
              onChange={(e) => setPlanRetention(e.target.value)}
            />
            <input
              placeholder="Features, comma separated — recording, export, evidence"
              value={planFeatures}
              onChange={(e) => setPlanFeatures(e.target.value)}
            />
            <div className="inline">
              <button
                className="primary"
                disabled={!planCode.trim() || !planName.trim()}
                onClick={() => void createPlan()}
              >
                Create plan
              </button>
            </div>
          </div>
        )}

        {plans.length === 0 ? (
          <p className="hint">
            No plans yet. Until one is sold to an account, nothing on this deployment
            expires and no feature is withheld.
          </p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Price</th>
                  <th>Retention</th>
                  <th>Features</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => (
                  <tr key={p.id} className={p.active ? undefined : 'muted'}>
                    <td>
                      <strong>{p.name}</strong>
                      <div className="sub">{p.code}</div>
                    </td>
                    <td className="sub">฿{baht(p.priceMonthlySatang)} /camera/month</td>
                    <td className="sub">{p.retentionDays} days</td>
                    <td className="sub">
                      {p.features.length ? p.features.join(', ') : 'none'}
                    </td>
                    <td>
                      <button
                        onClick={() => void act(() => api.setPlanActive(p.id, !p.active))}
                      >
                        {p.active ? 'Withdraw' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="hint">
          Withdrawing a plan only hides it from the picker. Accounts already on it keep
          exactly what they were sold — the record of what that was is the thing asked
          for at renewal.
        </p>
      </section>

      <section className="card">
        <h2>What running out actually does</h2>
        <p className="hint">
          Nothing here takes a broadcast off air. A stream already running keeps running
          at every stage, and extending a date restores service on the next heartbeat with
          no visit and no re-enrolment.
        </p>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>After</th>
                <th>What happens</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="sub">the date passes</td>
                <td>Console says expired. Nothing is withheld.</td>
              </tr>
              <tr>
                <td className="sub">7 days</td>
                <td>Exports and reports are withheld. Streaming and recording continue.</td>
              </tr>
              <tr>
                <td className="sub">14 days</td>
                <td>New recordings kept 1 day. Footage already on disk is not deleted.</td>
              </tr>
              <tr>
                <td className="sub">30 days</td>
                <td>
                  No configuration and no commands are served. The device still checks in,
                  and its token still works.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="hint">
          Suspending an account skips straight to the last row — that is a decision
          somebody made, not a date arriving. Every date here is read on the server&rsquo;s
          clock, the same one the heartbeat uses.
        </p>
      </section>
    </div>
  )
}
